import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Pencil, Search, Trash2, UserCog } from "lucide-react";
import { dashboardToast } from "../../utils/dashboardToast";
import {
  isPasswordPolicyCompliant,
  PASSWORD_POLICY_ERROR_MESSAGE,
  PASSWORD_POLICY_HINT,
} from "../../utils/passwordPolicy";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import ContentLoader from "../../components/ContentLoader";
import SaListPageShell from "../../components/sa/SaListPageShell";
import RecordDetailModal from "../../components/RecordDetailModal";
import CatalogFilterChip from "../../components/sa/CatalogFilterChip";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button as UiButton, Pagination as UiPagination } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";

import { getUserList, addOrUpdateUser, deleteUser } from "../../services/userApi";
import { getRoleList } from "../../services/roleApi";
import { getAllProjects } from "../../services/projectApi";
import "../../assets/css/dashboard.css";

const PROJECT_SCOPED_ROLES = new Set(["Client", "SuperUser"]);

const EMPTY_USER_FORM = {
  id: 0,
  roleId: "",
  projectId: "",
  userName: "",
  emailAddress: "",
  password: "",
  isActive: true,
};

const EmptyCell = () => <span className="app-td__empty">—</span>;

const User = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [searchParams] = useSearchParams();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState({ ...EMPTY_USER_FORM });
  const [formKey, setFormKey] = useState(0);
  // Dummy data for users, roles, and branches
  // const dummyRoles = [
  //   { id: 1, roleName: "Admin" },
  //   { id: 2, roleName: "Manager" },
  //   { id: 3, roleName: "Employee" },
  // ];
  // const dummyBranches = [
  //   { id: 1, branchName: "New York" },
  //   { id: 2, branchName: "London" },
  //   { id: 3, branchName: "Tokyo" },
  // ];
  // const dummyUsers = [
  //   {
  //     id: 1,
  //     employeeId: "EMP001",
  //     roleId: 1,
  //     branchId: 1,
  //     firstName: "John",
  //     lastName: "Doe",
  //     emailAddress: "john.doe@example.com",
  //     password: "password123",
  //     isActive: true,
  //   },
  //   {
  //     id: 2,
  //     employeeId: "EMP002",
  //     roleId: 2,
  //     branchId: 2,
  //     firstName: "Jane",
  //     lastName: "Smith",
  //     emailAddress: "jane.smith@example.com",
  //     password: "password456",
  //     isActive: false,
  //   },
  // ];

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
//   const isAuthenticated = useSelector(
//     (state) => state.auth.isAuthenticated
//   );
  const rolesFetchedRef = useRef(false);
  const branchesFetchedRef = useRef(false);
  const dataFetchedRef = useRef(false);
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);
  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize:   10,
    totalPages: 1,
    totalCount: 0,
  });    

  const refreshRoles = useCallback(async () => {
    try {
      const { items } = await getRoleList(1, 500);
      setRoles(items || []);
    } catch (err) {
      console.error("Failed to load roles:", err);
      dashboardToast.error("Failed to load roles for the form");
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const items = await getAllProjects();
      setProjects(items || []);
    } catch (err) {
      console.error("Failed to load projects:", err);
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!rolesFetchedRef.current) {
      rolesFetchedRef.current = true;
      refreshRoles();
    }
  }, [refreshRoles]);

  const selectedRole = roles.find((role) => String(role.id) === String(formData.roleId));
  const requiresProject =
    !formData.id && selectedRole && PROJECT_SCOPED_ROLES.has(selectedRole.roleName);

  const getProjectLabel = (project) => {
    const company = project.companyName ? `${project.companyName} — ` : "";
    const number = project.projectNumber ? ` (${project.projectNumber})` : "";
    return `${company}${project.projectName || "Unnamed project"}${number}`;
  };

  const refreshUsers = useCallback(async (
    pageNumber = apiPageInfo.pageNumber,
    pageSize = apiPageInfo.pageSize,
    sort = sortKey,
    search = searchTerm,
    roleId = roleFilter
  ) => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const page = await getUserList(pageNumber, pageSize, sort, search, roleId);
      setUsers(page.items || []);
      setApiPageInfo((prev) => ({
        ...prev,
        pageNumber: page.pageNumber,
        pageSize: page.pageSize,
        totalPages: Math.max(1, Math.ceil((page.totalCount || 0) / (page.pageSize || pageSize))),
        totalCount: page.totalCount || 0,
      }));
    } catch (err) {
      console.error("Failed to load users:", err);
      setUsers([]);
      setUsersError(err?.message || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.pageSize, sortKey, searchTerm, roleFilter]);

  useEffect(() => {
    const roleFromUrl = searchParams.get("roleId") || "";
    if (roleFromUrl) {
      setRoleFilter(roleFromUrl);
      refreshUsers(1, apiPageInfo.pageSize, sortKey, searchTerm, roleFromUrl);
      return;
    }
    refreshUsers(apiPageInfo.pageNumber, apiPageInfo.pageSize);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      refreshUsers(1, apiPageInfo.pageSize, sortKey, searchTerm, roleFilter);
    }, 300);

    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line
  }, [searchTerm]);

    const goToPage = useCallback(n => {
      const pg = Math.min(Math.max(n, 1), apiPageInfo.totalPages);
      if (pg !== apiPageInfo.pageNumber) {
        refreshUsers(pg, apiPageInfo.pageSize);
      }
    }, [apiPageInfo.pageNumber, apiPageInfo.pageSize, apiPageInfo.totalPages, refreshUsers]);

  const handleSortChange = (nextSort) => {
    setSortKey(nextSort);
    refreshUsers(1, apiPageInfo.pageSize, nextSort, searchTerm, roleFilter);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRoleFilterChange = (nextRole: string) => {
    setRoleFilter(nextRole);
    refreshUsers(1, apiPageInfo.pageSize, sortKey, searchTerm, nextRole);
  };

  const handleShow = useCallback((user = null) => {
    if (user) {
      setFormData({
        id: user.id,
        roleId: user.roleId || "",
        projectId: "",
        userName: user.userName || "",
        emailAddress: user.emailAddress || "",
        password: "",
        isActive: user.isActive === true
      });
    } else {
      setFormData({ ...EMPTY_USER_FORM });
      setFormKey((prev) => prev + 1);
      refreshProjects();
    }
    setShowModal(true);
  }, [refreshProjects]);

  const handleClose = useCallback(() => {
    setShowModal(false);
    setFormData({ ...EMPTY_USER_FORM });
  }, []);


  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const nextValue = type === "checkbox" ? checked : value;
      const next = {
        ...prev,
        [name]: nextValue,
      };

      if (name === "roleId") {
        const role = roles.find((item) => String(item.id) === String(nextValue));
        if (!role || !PROJECT_SCOPED_ROLES.has(role.roleName)) {
          next.projectId = "";
        }
      }

      return next;
    });
  }, [roles]);


const handleSubmit = useCallback(
  async (e) => {
    e.preventDefault();
    const {
      roleId,
      projectId,
      userName,
      emailAddress,
      password,
      isActive
    } = formData;

    if (requiresProject && !projectId) {
      dashboardToast.error("Please select a project for Client or Super User accounts.");
      return;
    }

    const shouldValidatePassword = !formData.id || Boolean(password?.trim());
    if (shouldValidatePassword && !isPasswordPolicyCompliant(password || "")) {
      dashboardToast.error(PASSWORD_POLICY_ERROR_MESSAGE);
      return;
    }

    if (roleId && userName && emailAddress && (!formData.id ? password : true)) {
      try {
        const payload = {
          id: formData.id,
          roleId: Number(roleId),
          username: userName,
          emailAddress: emailAddress,
          ...(password ? { password } : {}),
          isActive: isActive,
        };

        if (projectId) {
          payload.projectId = Number(projectId);
        }

        await addOrUpdateUser(payload);
        refreshUsers();
        handleClose();
        dashboardToast.success(
          formData.id ? "User updated successfully" : "User added successfully"
        );
      } catch (err) {
        console.error(err);
        dashboardToast.error(err?.message || "Failed to save user");
      }
    }
  },
  [formData, requiresProject, refreshUsers, handleClose]
);


  const handleDelete = useCallback((user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  }, []);

  const openViewModal = useCallback((user) => {
    setViewRecord(user);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
  }, []);
  const confirmDelete = useCallback(async () => {
    if (!userToDelete?.id) {
      return;
    }

    const deletedId = userToDelete.id;
    const shouldGoToPreviousPage = users.length === 1 && apiPageInfo.pageNumber > 1;
    const refreshPage = shouldGoToPreviousPage
      ? apiPageInfo.pageNumber - 1
      : apiPageInfo.pageNumber;

    setDeleteLoading(true);
    try {
      await deleteUser(deletedId);

      // Remove immediately so the table updates without waiting for the refetch.
      setUsers((prev) => prev.filter((user) => user.id !== deletedId));
      setApiPageInfo((prev) => ({
        ...prev,
        totalCount: Math.max(0, prev.totalCount - 1),
      }));

      await refreshUsers(refreshPage, apiPageInfo.pageSize);
      dashboardToast.success("User deleted successfully");
    } catch (err) {
      console.error(err);
      dashboardToast.error(err?.message || "Failed to delete user");
      await refreshUsers(refreshPage, apiPageInfo.pageSize);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  }, [userToDelete, users.length, apiPageInfo.pageNumber, apiPageInfo.pageSize, refreshUsers]);
  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  }, []);

return (
  <DashboardShell
    isMenuCollapsed={isMenuCollapsed}
    sidebar={
      <Menu
        onSelect={handleMenuSelection}
        selectedItem={selectedItem}
        isCollapsed={isMenuCollapsed}
        onToggleCollapse={toggleMenuCollapse}
      />
    }
  >
    <SaListPageShell topBarTitle="User Management">
      <div className="dash-frame-parent1">
        <div className="dash-list-toolbar">
          <div className="dash-list-toolbar__intro">
            <h5 className="dash-list-toolbar__title">Manage Users</h5>
            <p className="dash-list-toolbar__desc">
              Create and manage platform users, roles, and account status.
            </p>
          </div>
          <div className="dash-list-toolbar__actions">
            <div className="dash-search-field dash-table-search">
              <Search className="dash-search-field__icon" aria-hidden />
              <input
                type="text"
                placeholder="Search user, role, email…"
                value={searchTerm}
                onChange={handleSearchChange}
                className="dash-search-field__input"
                disabled={usersLoading}
              />
            </div>
            <CatalogFilterChip
              label="Role"
              value={String(roleFilter || "")}
              onChange={handleRoleFilterChange}
              disabled={usersLoading}
              options={[
                ...roles
                  .slice()
                  .sort((left, right) =>
                    String(left.roleName || "").localeCompare(
                      String(right.roleName || ""),
                      undefined,
                      { sensitivity: "base" }
                    )
                  )
                  .map((role) => ({
                    value: String(role.id),
                    label: role.roleName,
                  })),
              ]}
            />
            <TableSortSelect variant="chip"
              value={sortKey}
              onChange={handleSortChange}
              disabled={usersLoading}
            />
            <UiButton variant="primary" className="sa-create-btn" onClick={() => handleShow()}>
              Create User
            </UiButton>
          </div>
        </div>

        {usersLoading ? (
          <ContentLoader label="Loading users…" />
        ) : usersError ? (
          <Alert variant="error">{usersError}</Alert>
        ) : (
          <Fragment>
            <div className="app-table-wrap app-table-wrap--refined">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Role</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center">
                        <span className="app-td__desc">No users found.</span>
                      </td>
                    </tr>
                  ) : (
                    users.map((user, index) => {
                      const rowNumber =
                        (apiPageInfo.pageNumber - 1) * apiPageInfo.pageSize + index + 1;
                      const displayName = user.userName || "Untitled user";

                      return (
                        <tr
                          key={user.id}
                          className="rdm-row-clickable"
                          onClick={() => openViewModal(user)}
                        >
                          <td>
                            <span className="app-td__index">{rowNumber}</span>
                          </td>
                          <td>
                            <span className="app-td__title">{displayName}</span>
                          </td>
                          <td>
                            {user.roleName ? (
                              <span className="app-td__desc">{user.roleName}</span>
                            ) : (
                              <EmptyCell />
                            )}
                          </td>
                          <td>
                            {user.emailAddress ? (
                              <span className="app-td__meta">{user.emailAddress}</span>
                            ) : (
                              <EmptyCell />
                            )}
                          </td>
                          <td>
                            <span
                              className={
                                user.isActive
                                  ? "app-status app-status--success"
                                  : "app-status app-status--muted"
                              }
                            >
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td
                            className="dash-mobile-table-actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="btn-list-actions">
                              <UiButton
                                variant="outline"
                                size="sm"
                                className="btn-list-edit"
                                title="Edit"
                                aria-label={`Edit ${displayName}`}
                                onClick={() => handleShow(user)}
                              >
                                <Pencil />
                              </UiButton>
                              <UiButton
                                variant="danger"
                                size="sm"
                                className="btn-list-delete"
                                title="Delete"
                                aria-label={`Delete ${displayName}`}
                                onClick={() => handleDelete(user)}
                              >
                                <Trash2 />
                              </UiButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <UiPagination
              page={apiPageInfo.pageNumber}
              totalPages={apiPageInfo.totalPages}
              onPageChange={goToPage}
            />
          </Fragment>
        )}
      </div>
    </SaListPageShell>

    {/* Create/Edit Modal */}
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      dialogClassName="um-user-form-dialog rounded-3"
      contentClassName="um-user-form-modal"
    >
      <Modal.Header closeButton className="dashboard-modal-header">
        <UserCog className="h-5 w-5 me-2 " />
        <Modal.Title>{formData.id ? "Edit User" : "Create User"}</Modal.Title>
      </Modal.Header>

      <Form key={formData.id ? `edit-user-${formData.id}` : `create-user-${formKey}`} onSubmit={handleSubmit} autoComplete="off">
        <Modal.Body className="px-4 um-user-form-body">
          <Row>
            {/* Role */}
            <Col md={6}>
              <Form.Group controlId="formRoleId" className="mb-3">
                <Form.Label className="required">Role</Form.Label>
                <Form.Select
                  name="roleId"
                  value={formData.roleId}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.roleName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            {/* User Name */}
            <Col md={6}>
              <Form.Group controlId="formUserName" className="mb-3">
                <Form.Label className="required">User Name</Form.Label>
                <Form.Control
                  type="text"
                  name="userName"
                  value={formData.userName}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            {/* Email */}
            <Col md={6}>
              <Form.Group controlId="formEmail" className="mb-3">
                <Form.Label className="required">Email</Form.Label>
                <Form.Control
                  type="email"
                  name="emailAddress"
                  value={formData.emailAddress}
                  onChange={handleChange}
                  autoComplete="off"
                  required
                />
              </Form.Group>
            </Col>
            {/* Password */}
            <Col md={6}>
              <Form.Group controlId="formPassword" className="mb-3">
                <Form.Label className={!formData.id ? "required" : undefined}>
                  {formData.id ? "New Password (optional)" : "Password"}
                </Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  required={!formData.id}
                />
                <Form.Text className="text-muted">{PASSWORD_POLICY_HINT}</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          {requiresProject && (
            <Row>
              <Col md={12}>
                <Form.Group controlId="formProjectId" className="mb-3">
                  <Form.Label className="required">Project</Form.Label>
                  <Form.Select
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleChange}
                    autoComplete="off"
                    required
                    disabled={projectsLoading}
                  >
                    <option value="">
                      {projectsLoading ? "Loading projects..." : "Select project"}
                    </option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {getProjectLabel(project)}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Client and Super User accounts must be linked to a project so they can access the correct workspace after sign-in.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
          {/* Active Checkbox */}
          <Row>
            <Col md={6} className="d-flex align-items-center">
              <Form.Group controlId="formIsActive" className="mb-3 mt-4">
                <Form.Check
                  type="checkbox"
                  label="Active user"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button className="btn btn-dashboard-cancel" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            className="btn btn-dashboard-primary"
          >
            Save User
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>

    {/* Delete Confirmation */}
    <Modal show={showDeleteModal} onHide={cancelDelete} centered>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Are you sure you want to delete{" "}
        <strong>
          {userToDelete?.userName || userToDelete?.emailAddress}
        </strong>
        ?
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={cancelDelete} disabled={deleteLoading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={confirmDelete} disabled={deleteLoading}>
          {deleteLoading ? "Deleting..." : "Delete"}
        </Button>
      </Modal.Footer>
    </Modal>

    <RecordDetailModal
      show={showViewModal}
      onHide={closeViewModal}
      title={viewRecord?.userName || "User Details"}
      subtitle="User record"
      fields={[
        { label: "Role", value: viewRecord?.roleName },
        { label: "User Name", value: viewRecord?.userName },
        { label: "Email", value: viewRecord?.emailAddress },
        { label: "Active", value: Boolean(viewRecord?.isActive) },
      ]}
      actions={[
        {
          type: "edit",
          onClick: () => {
            const user = viewRecord;
            closeViewModal();
            if (user) handleShow(user);
          },
        },
        {
          type: "delete",
          onClick: () => {
            const user = viewRecord;
            closeViewModal();
            if (user) handleDelete(user);
          },
        },
      ]}
    />

    {/* ToastContainer moved to App.js */}
  </DashboardShell>
);
};

export default User;
